/**
 * SSBU Power Rankings - Public Ladder Page
 * Shows current SSBU player rankings with stats and challenge window status
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, doc, getDoc, orderBy, query } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import type { LadderEntry, LadderSettings } from '../types';

export default function LadderPage() {
    const { user } = useAuth();
    const [rankings, setRankings] = useState<LadderEntry[]>([]);
    const [settings, setSettings] = useState<LadderSettings | null>(null);
    const [loading, setLoading] = useState(true);
    const [expandedPlayer, setExpandedPlayer] = useState<string | null>(null);

    useEffect(() => {
        async function loadData() {
            try {
                // Load rankings
                const ladderQuery = query(
                    collection(db, 'ssbuLadder'),
                    orderBy('rank', 'asc')
                );
                const ladderSnap = await getDocs(ladderQuery);
                const ladderData = ladderSnap.docs.map(d => ({
                    id: d.id,
                    ...d.data()
                })) as LadderEntry[];
                setRankings(ladderData);

                // Load settings
                const settingsDoc = await getDoc(doc(db, 'settings', 'ssbuLadder'));
                if (settingsDoc.exists()) {
                    setSettings(settingsDoc.data() as LadderSettings);
                }
            } catch (error) {
                console.error('Error loading ladder:', error);
            } finally {
                setLoading(false);
            }
        }
        loadData();
    }, []);

    const getRankMovement = (current: number, previous?: number) => {
        if (!previous) return { icon: '🆕', class: 'text-blue-400', text: 'NEW' };
        const diff = previous - current;
        if (diff > 0) return { icon: '↑', class: 'text-green-400', text: `+${diff}` };
        if (diff < 0) return { icon: '↓', class: 'text-red-400', text: `${diff}` };
        return { icon: '─', class: 'text-secondary-500', text: '' };
    };

    const getStreakDisplay = (streak: number) => {
        if (streak === 0) return null;
        if (streak > 0) return { text: `🔥 ${streak}W`, class: 'text-orange-400' };
        return { text: `❄️ ${Math.abs(streak)}L`, class: 'text-blue-400' };
    };

    const getRankStyle = (rank: number) => {
        if (rank === 1) return 'bg-gradient-to-r from-yellow-500/20 to-yellow-600/10 border-l-4 border-yellow-500';
        if (rank === 2) return 'bg-gradient-to-r from-gray-400/20 to-gray-500/10 border-l-4 border-gray-400';
        if (rank === 3) return 'bg-gradient-to-r from-orange-500/20 to-orange-600/10 border-l-4 border-orange-500';
        if (rank <= 6) return 'bg-gradient-to-r from-red-500/10 to-transparent border-l-4 border-red-500/50';
        return 'bg-secondary-800/50 border-l-4 border-secondary-600';
    };

    const getRankBadge = (rank: number) => {
        if (rank === 1) return '👑';
        if (rank === 2) return '🥈';
        if (rank === 3) return '🥉';
        return `#${rank}`;
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <div className="w-16 h-16 border-4 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-secondary-400">Loading Power Rankings...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Header */}
            <div className="text-center mb-8">
                <div className="text-5xl mb-4">⚔️</div>
                <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-2">
                    SSBU <span className="text-red-500">Power Rankings</span>
                </h1>
                <p className="text-secondary-400 mb-4">
                    Peck Pirates Internal Ladder • Crews & Solos
                </p>

                {/* Challenge Window Status */}
                {settings && (
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${settings.challengeWindowOpen
                        ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                        : 'bg-secondary-700 text-secondary-400 border border-secondary-600'
                        }`}>
                        <span className={`w-2 h-2 rounded-full ${settings.challengeWindowOpen ? 'bg-green-400 animate-pulse' : 'bg-secondary-500'}`}></span>
                        {settings.challengeWindowOpen ? (
                            <>
                                Challenge Window OPEN
                                {settings.currentWindowEnd && (
                                    <span className="text-secondary-400 ml-1">
                                        • Closes {new Date(settings.currentWindowEnd.toDate()).toLocaleDateString()}
                                    </span>
                                )}
                            </>
                        ) : (
                            'Challenge Window Closed'
                        )}
                    </div>
                )}
            </div>

            {/* Legend */}
            <div className="card mb-6 p-4">
                <div className="flex flex-wrap gap-4 text-sm text-secondary-400">
                    <div className="flex items-center gap-2">
                        <span className="text-yellow-500">👑</span> #1 Ranked
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="w-4 h-4 bg-red-500/30 border-l-2 border-red-500 rounded-sm"></span> Top 6 (Varsity Pool)
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-green-400">↑</span> Rank Up
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-red-400">↓</span> Rank Down
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="text-orange-400">🔥</span> Win Streak
                    </div>
                </div>
            </div>

            {/* Rankings List */}
            <div className="space-y-2 mb-8">
                {rankings.length === 0 ? (
                    <div className="card text-center py-12">
                        <p className="text-secondary-400 text-lg">No rankings yet</p>
                        <p className="text-secondary-500 text-sm mt-2">Check back when the ladder is initialized</p>
                    </div>
                ) : (
                    rankings.map((player) => {
                        const movement = getRankMovement(player.rank, player.previousRank);
                        const streak = getStreakDisplay(player.stats.currentStreak);
                        const isExpanded = expandedPlayer === player.id;

                        return (
                            <div
                                key={player.id}
                                className={`rounded-lg overflow-hidden transition-all duration-200 ${getRankStyle(player.rank)}`}
                            >
                                <button
                                    onClick={() => setExpandedPlayer(isExpanded ? null : player.id)}
                                    className="w-full p-4 flex items-center gap-4 hover:bg-white/5 transition-colors text-left"
                                >
                                    {/* Rank */}
                                    <div className="flex-shrink-0 w-12 text-center">
                                        <span className={`text-2xl ${player.rank <= 3 ? '' : 'font-bold text-secondary-300'}`}>
                                            {getRankBadge(player.rank)}
                                        </span>
                                    </div>

                                    {/* Movement Arrow */}
                                    <div className={`flex-shrink-0 w-8 text-center font-mono text-sm ${movement.class}`}>
                                        {movement.icon} {movement.text}
                                    </div>

                                    {/* Player Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold text-white">
                                                {player.gamerTag || player.playerName}
                                            </span>
                                            {player.gamerTag && (
                                                <span className="text-secondary-500 text-sm">({player.playerName})</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Streak */}
                                    {streak && (
                                        <div className={`flex-shrink-0 ${streak.class} text-sm font-medium`}>
                                            {streak.text}
                                        </div>
                                    )}

                                    {/* Quick Stats */}
                                    <div className="flex-shrink-0 flex items-center gap-4 text-sm text-secondary-400">
                                        <span title="Challenges Won">
                                            ⚔️ {player.stats.challengesWon}/{player.stats.challengesIssued}
                                        </span>
                                        <span title="Defenses Won">
                                            🛡️ {player.stats.defensesWon}/{player.stats.defensesPlayed}
                                        </span>
                                    </div>

                                    {/* Expand Arrow */}
                                    <div className="flex-shrink-0 text-secondary-500">
                                        <svg
                                            className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </div>
                                </button>

                                {/* Expanded Stats */}
                                {isExpanded && (
                                    <div className="px-4 pb-4 pt-0 border-t border-secondary-700/50">
                                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                                            <div className="bg-secondary-800/50 rounded-lg p-3 text-center">
                                                <div className="text-2xl font-bold text-white">{player.stats.challengesWon}</div>
                                                <div className="text-xs text-secondary-400">Challenges Won</div>
                                            </div>
                                            <div className="bg-secondary-800/50 rounded-lg p-3 text-center">
                                                <div className="text-2xl font-bold text-white">{player.stats.defensesWon}</div>
                                                <div className="text-xs text-secondary-400">Defenses Won</div>
                                            </div>
                                            <div className="bg-secondary-800/50 rounded-lg p-3 text-center">
                                                <div className="text-2xl font-bold text-white">#{player.stats.bestRank}</div>
                                                <div className="text-xs text-secondary-400">Best Rank</div>
                                            </div>
                                            <div className="bg-secondary-800/50 rounded-lg p-3 text-center">
                                                <div className="text-2xl font-bold text-white">{player.stats.weeksAtRank}</div>
                                                <div className="text-xs text-secondary-400">Weeks at Rank</div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {/* Challenge Rules */}
            <div className="card mb-8">
                <h3 className="text-lg font-display font-bold text-white mb-4 flex items-center gap-2">
                    📜 Challenge Rules
                </h3>
                <div className="space-y-3 text-sm text-secondary-300">
                    <div className="flex gap-3">
                        <span className="text-red-400">•</span>
                        <span>Players ranked <strong className="text-white">6th or lower</strong> can challenge up to <strong className="text-white">3 ranks</strong> ahead</span>
                    </div>
                    <div className="flex gap-3">
                        <span className="text-red-400">•</span>
                        <span>Players ranked <strong className="text-white">5th or higher</strong> can only challenge <strong className="text-white">1 rank</strong> ahead</span>
                    </div>
                    <div className="flex gap-3">
                        <span className="text-red-400">•</span>
                        <span>If multiple players want to challenge the same defender, the <strong className="text-white">closest rank</strong> gets priority</span>
                    </div>
                    <div className="flex gap-3">
                        <span className="text-green-400">•</span>
                        <span><strong className="text-white">If you win:</strong> You take the defender's rank. The defender and everyone between shifts down 1 spot.</span>
                    </div>
                    <div className="flex gap-3">
                        <span className="text-blue-400">•</span>
                        <span><strong className="text-white">If you lose:</strong> Everyone keeps their current rank. No changes to the ladder.</span>
                    </div>
                    <div className="flex gap-3">
                        <span className="text-red-400">•</span>
                        <span>Default format: <strong className="text-white">Best of 3</strong> (or whatever both players agree on)</span>
                    </div>
                </div>
            </div>

            {/* How It Works */}
            <div className="card mb-8 bg-gradient-to-r from-secondary-800 to-secondary-900 border-secondary-600">
                <h3 className="text-lg font-display font-bold text-white mb-4 flex items-center gap-2">
                    ❓ How the Ladder Works
                </h3>
                <div className="space-y-4 text-sm text-secondary-300">
                    <div>
                        <h4 className="text-white font-semibold mb-2">📅 Challenge Windows</h4>
                        <p>Challenges can only be submitted when the window is <span className="text-green-400 font-medium">OPEN</span>. Check the status at the top of this page. Windows typically open Monday and close Friday.</p>
                    </div>
                    <div>
                        <h4 className="text-white font-semibold mb-2">⚔️ Making a Challenge</h4>
                        <p>Log in to your Player Dashboard and find the Ladder section. You'll see who you can challenge based on your current rank. Submit your challenge and coordinate with your opponent to schedule the match.</p>
                    </div>
                    <div>
                        <h4 className="text-white font-semibold mb-2">📊 Reporting Match Results</h4>
                        <p className="mb-2">After your match, report the results to Coach:</p>
                        <ul className="list-disc list-inside space-y-1 text-secondary-400">
                            <li>In-person at practice</li>
                            <li>Message in the <span className="text-purple-400">#ssbu-ladder</span> Discord channel</li>
                            <li>DM Coach directly on Discord</li>
                        </ul>
                        <p className="mt-2 text-yellow-400/80">Include: Winner, Score (e.g., 2-1), and any disputes.</p>
                    </div>
                    <div>
                        <h4 className="text-white font-semibold mb-2">🏆 Example</h4>
                        <p className="text-secondary-400">
                            If <span className="text-white">Rank #7</span> challenges and beats <span className="text-white">Rank #4</span>:
                        </p>
                        <ul className="list-disc list-inside mt-1 text-secondary-400">
                            <li>Challenger moves to <span className="text-green-400">#4</span></li>
                            <li>Former #4 shifts to <span className="text-red-400">#5</span></li>
                            <li>Former #5 shifts to <span className="text-red-400">#6</span></li>
                            <li>Former #6 shifts to <span className="text-red-400">#7</span></li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* CTA */}
            <div className="text-center">
                {user ? (
                    <Link to="/player" className="btn-primary">
                        Go to Dashboard →
                    </Link>
                ) : (
                    <Link to="/login" className="btn-primary">
                        Log in to Issue a Challenge
                    </Link>
                )}
            </div>
        </div>
    );
}
